import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";

import { CustomInput } from "src/component";
import { renderAlert } from "src/utils/functions";
import { createInventory, updateInventory } from "src/services/api";
import CustomSelect from "src/component/CustomSelect";
import { Button } from "src/components/ui/button";

const CreateInventory = ({
  editData,
  refetch,
  setModalOpen,
  rawLocations,
  rawBrands,
}) => {
  const { mutate, isLoading } = useMutation(
    editData ? updateInventory : createInventory,
    {
      onSuccess: (res) => {
        renderAlert({
          title: "Success",
          msg: res?.msg,
          type: "success",
        }).then((result) => {
          if (result) {
            setModalOpen(false);
          } else {
            setModalOpen(false);
          }
        });
        refetch();
      },
    }
  );

  const locations = rawLocations?.data;
  const brands = rawBrands?.data;

  const initialValues = {
    name: editData?.name ?? "",
    quantity: editData?.quantity ?? "",
    cost_price: editData?.cost_price ?? "",
    selling_price: editData?.selling_price ?? "",
    location_id: editData?.location_id ?? "",
    brand_id: editData?.brand_id ?? "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    quantity: Yup.number()
      .typeError("Quantity must be a number")
      .required("Quantity is required")
      .integer("Quantity must be an integer")
      .moreThan(0, "Quantity must be greater than zero"),
    cost_price: Yup.number()
      .typeError("Cost price must be a number")
      .required("Cost price is required")
      .min(0, "Cost price cannot be negative"),
    selling_price: Yup.number()
      .typeError("Selling price must be a number")
      .required("Selling price is required")
      .min(0, "Selling price cannot be negative"),
    location_id: Yup.string().required("Location is required"),
    brand_id: Yup.string().required("Brand is required"),
  });

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          mutate({ id: editData?.id, ...values });
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="px-[3rem] py-[2rem]">
              <CustomInput
                title="Name"
                name="name"
                placeholder="Enter Name"
                onChange={handleChange}
                value={values.name}
                onBlur={handleBlur}
                formType="fill"
                isRequired={true}
              />
              <CustomInput
                title="Quantity"
                name="quantity"
                type="number"
                placeholder="Enter Quantity"
                onChange={handleChange}
                value={values.quantity}
                onBlur={handleBlur}
                formType="fill"
                isRequired={true}
              />
              {!editData && (
                <div className="overflow-visible">
                  <CustomInput
                    title="Cost Price"
                    name="cost_price"
                    type="number"
                    placeholder="Enter Cost Price"
                    onChange={handleChange}
                    value={values.cost_price}
                    onBlur={handleBlur}
                    formType="fill"
                    isRequired={true}
                  />
                  <CustomInput
                    title="Selling Price"
                    name="selling_price"
                    type="number"
                    placeholder="Enter Selling Price"
                    onChange={handleChange}
                    value={values.selling_price}
                    onBlur={handleBlur}
                    formType="fill"
                    isRequired={true}
                  />

                  <CustomSelect
                    label="Location"
                    name="location_id"
                    value={values.location_id}
                    onChange={(val) => setFieldValue("location_id", val)}
                    options={locations && locations?.map(({ id, name }) => ({
                      label: name,
                      value: String(id),
                    }))}
                    placeholder="Select location"
                    required
                  />
                  <CustomSelect
                    label="Brand"
                    name="brand_id"
                    value={values.brand_id}
                    onChange={(val) => setFieldValue("brand_id", val)}
                    options={brands && brands?.map(({ id, name }) => ({
                      label: name,
                      value: String(id),
                    }))}
                    placeholder="Select brand"
                    required
                  />
                </div>
              )}
            </div>

            <div className="border-t border-t-gray">
              <div className="flex justify-end px-[3rem] py-[2rem]">
                <Button
                  disabled={isLoading}
                  type="submit"
                  className="btn-loader flex justify-center items-center rounded-[5px] font-base py-[2rem] px-[4rem] h-[3.5rem]"
                >
                  {editData ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default CreateInventory;
